import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { addEventToTracking } from "@website/tracking/user";
import { getCarrierByPhoneNumber } from "@website/utils/mobile-carrier-map";
import { OtpCardPropertiesType } from "@website/widget/login/components/otp-card/otp-card.types";
import { AuthRequestOtpByCallService, AuthRequestOtpService } from "@website/widget/login/services/auth-request-otp";
import { AuthVerifyOtpService } from "@website/widget/login/services/auth-verify-otp";
import { ResetPasswordRequestOtpService } from "@website/widget/login/services/reset-password-request-otp";
import { useEffect, useRef, useState } from "react";
export const useOtpCard = (properties: OtpCardPropertiesType) => {
  const {
    userData,
    onEndAuth,
    setStep,
    setUserData
  } = properties;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingSendAgain, setIsLoadingSendAgain] = useState<boolean>(false);
  const [isLoadingSendAgainByCall, setIsLoadingSendAgainByCall] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [showSendAgain, setShowSendAgain] = useState<boolean>(false);
  const otpReference = useRef<string>("");
  const isSubmittingReference = useRef(false);
  const lastSubmittedOtpReference = useRef<string>("");
  const onChangeOtp = (value: string) => {
    setOtp(value);
    otpReference.current = value;
    const otpLength = userData?.otpLength || 0;
    if (value.length < otpLength) {
      lastSubmittedOtpReference.current = "";
    }
  };
  const onSubmitOtp = async (_event?: React.FormEvent<HTMLFormElement>, providedOtp?: string) => {
    _event?.preventDefault();
    const {
      otpId,
      otpLength: otpLength_0
    } = userData;
    const otpValue = providedOtp ?? otpReference.current;
    if (otpValue.length !== otpLength_0) return;

    // prevent double submit (autofill + effect / fast typing)
    if (isSubmittingReference.current) return;
    isSubmittingReference.current = true;
    setIsLoading(true);
    setError("");
    addEventToTracking(USER_TRACKING_EVENTS.OTP_ENTERED, {
      product: "core",
      carrier: getCarrierByPhoneNumber(userData.phoneNumber)
    });
    if (!otpId) {
      addEventToTracking(USER_TRACKING_EVENTS.OTP_ENTERED_RESULT, {
        product: "core",
        result: "NotEnterd",
        carrier: getCarrierByPhoneNumber(userData.phoneNumber)
      });
      setError("کد را وارد کنید");
      setIsLoading(false);
      isSubmittingReference.current = false;
      return;
    }
    AuthVerifyOtpService({
      code: otpValue,
      otpId
    }).then(data => {
      const {
        accessToken,
        hasPassword,
        refreshToken,
        userExists
      } = data;
      if (accessToken) {
        setUserData({
          ...userData,
          accessToken,
          hasPassword,
          refreshToken,
          userExists
        });
        onEndAuth?.(accessToken, refreshToken);
        addEventToTracking(USER_TRACKING_EVENTS.OTP_ENTERED_RESULT, {
          product: "core",
          result: "Successed",
          carrier: getCarrierByPhoneNumber(userData.phoneNumber)
        });
      } else {
        // if backend returns no token, allow retry
        isSubmittingReference.current = false;
      }
    }).catch(error_0 => {
      setError(error_0?.response?.message || "خطایی رخ داده، دوباره تلاش کنید.");
      addEventToTracking(USER_TRACKING_EVENTS.OTP_ENTERED_RESULT, {
        product: "core",
        result: "NotCorrect",
        carrier: getCarrierByPhoneNumber(userData.phoneNumber)
      });
      isSubmittingReference.current = false;
    }).finally(() => setIsLoading(false));
  };
  const onSubmitOtpForgotPassword = async (_event_0?: React.FormEvent<HTMLFormElement>, providedOtp_0?: string) => {
    _event_0?.preventDefault();
    const {
      phoneNumber,
      otpLength: otpLength_1
    } = userData;
    const otpValue_0 = providedOtp_0 ?? otpReference.current;
    if (otpValue_0.length !== otpLength_1) {
      setError("کد را وارد کنید");
      return;
    }
    if (isSubmittingReference.current) return;
    isSubmittingReference.current = true;
    setIsLoading(true);
    setError("");
    ResetPasswordRequestOtpService({
      phoneNumber
    }).then(data_0 => {
      setUserData({
        ...userData,
        ...data_0,
        otp: otpValue_0
      });
      setStep("forgot-password");
    }).catch(error_1 => {
      setError(error_1?.response?.message || "خطایی رخ داده، دوباره تلاش کنید.");
      isSubmittingReference.current = false;
    }).finally(() => setIsLoading(false));
  };
  const onTimerEnd = () => {
    setShowSendAgain(true);
  };
  const onClickSendAgain = async () => {
    const {
      phoneNumber: phoneNumber_0
    } = userData;
    setIsLoadingSendAgain(true);
    setError("");
    isSubmittingReference.current = false;
    addEventToTracking(USER_TRACKING_EVENTS.OTP_NOT_DELIVERED, {
      product: "core",
      result: "SMS"
    });
    AuthRequestOtpService({
      phoneNumber: phoneNumber_0
    }).then(data_1 => {
      setUserData({
        ...userData,
        ...data_1
      });
      setShowSendAgain(false);
      setOtp("");
      otpReference.current = "";
      lastSubmittedOtpReference.current = "";
    }).catch(error_2 => {
      setError(error_2?.response?.message || "خطایی رخ داده، دوباره تلاش کنید.");
    }).finally(() => setIsLoadingSendAgain(false));
  };
  const onClickSendAgainByCall = async () => {
    const {
      phoneNumber: phoneNumber_1
    } = userData;
    setIsLoadingSendAgainByCall(true);
    setError("");
    isSubmittingReference.current = false;
    addEventToTracking(USER_TRACKING_EVENTS.OTP_NOT_DELIVERED, {
      product: "core",
      result: "TTS"
    });
    AuthRequestOtpByCallService({
      phoneNumber: phoneNumber_1
    }).then(data_2 => {
      setUserData({
        ...userData,
        ...data_2
      });
      setShowSendAgain(false);
      setOtp("");
      otpReference.current = "";
      lastSubmittedOtpReference.current = "";
    }).catch(error_3 => {
      setError(error_3?.response?.message || "خطایی رخ داده، دوباره تلاش کنید.");
    }).finally(() => setIsLoadingSendAgainByCall(false));
  };
  const onBackClick = () => {
    const {
      forgotPassword
    } = userData;
    isSubmittingReference.current = false;
    if (forgotPassword) setStep("password");else setStep("phone-number");
  };
  const onClickSupport = () => {
    addEventToTracking(USER_TRACKING_EVENTS.CALL_CS, {
      product: "core"
    });
  };
  useEffect(() => {
    if (typeof window === "undefined" || !("OTPCredential" in window) || !("credentials" in navigator)) {
      return;
    }
    const ac = new AbortController();
    let isMounted = true;
    async function listenForOtp() {
      try {
        // eslint-disable-next-line no-console
        console.log("WebOTP listening...");
        const cred = await navigator.credentials.get({
          // @ts-expect-error WebOTP typing
          otp: {
            transport: ["sms"]
          },
          signal: ac.signal
        });
        if (!isMounted) return;
        const code = (cred as any)?.code as string | undefined;
        if (!code) return;
        const clean = code.match(/\d+/)?.[0] ?? "";
        if (!clean) return;

        // eslint-disable-next-line no-console
        console.log("WebOTP received:", clean);
        setOtp(clean);
        otpReference.current = clean;
        const otpLength_2 = userData?.otpLength || 0;
        if (clean.length === otpLength_2) {
          if (userData?.forgotPassword) {
            onSubmitOtpForgotPassword(undefined, clean);
          } else {
            setTimeout(() => {
              onSubmitOtp(undefined, clean);
            }, 100);
          }
        }
      } catch (error_4) {
        // eslint-disable-next-line no-console
        console.log("WebOTP error:", error_4);
      }
    }
    listenForOtp();
    return () => {
      isMounted = false;
      ac.abort();
    };
  }, []);
  useEffect(() => {
    const otpLength_3 = userData?.otpLength || 0;
    if (!otpLength_3 || otp.length !== otpLength_3) {
      return;
    }
    if (otp === lastSubmittedOtpReference.current) {
      return;
    }
    lastSubmittedOtpReference.current = otp;
    if (userData?.forgotPassword) {
      onSubmitOtpForgotPassword(undefined, otp);
    } else {
      const submitTimeout = setTimeout(() => {
        onSubmitOtp(undefined, otp);
      }, 100);
      return () => clearTimeout(submitTimeout);
    }
  }, [otp, userData?.otpLength, userData?.forgotPassword, onSubmitOtp, onSubmitOtpForgotPassword]);
  useEffect(() => {
    if (userData?.phoneNumber) {
      addEventToTracking(USER_TRACKING_EVENTS.OTP_OPENED, {
        product: "core",
        carrier: getCarrierByPhoneNumber(userData.phoneNumber)
      });
    }
  }, [userData?.phoneNumber]);
  return {
    isLoading,
    otp,
    onChangeOtp,
    onSubmitOtp,
    error,
    onBackClick,
    onTimerEnd,
    showSendAgain,
    onClickSendAgain,
    isLoadingSendAgain,
    isLoadingSendAgainByCall,
    onClickSendAgainByCall,
    onSubmitOtpForgotPassword,
    onClickSupport
  };
};